import type { MobileAuthResponse } from '@/utils/scrub-oauth-params';

/** Android package of the iPractice app, as registered in the Play Store and in `assetlinks.json`. */
export const ANDROID_APP_PACKAGE = 'com.ipractice.myipractice';

/** Path the mobile app's B2C redirect lands on; the app claims it as a verified App Link. */
const MOBILE_AUTH_PATH = '/mobile-auth';

/**
 * Returns whether the user agent belongs to an Android browser, which is the
 * only platform where the browser can hand the sign-in response to the app by
 * package (see {@link buildAndroidAppIntentUrl}).
 *
 * @param userAgent - The value of `navigator.userAgent`.
 * @returns True for Android.
 */
export const isAndroidUserAgent = (userAgent: string): boolean => /android/i.test(userAgent);

/** What the intent URL needs: the portal host the response arrived on, and the response. */
export type AndroidAppIntentInput = MobileAuthResponse & {
  /** The host serving this page, for example `mijn.ipractice.nl`. */
  host: string;
};

/**
 * Builds the Android intent URL that hands the sign-in response to the
 * iPractice app from a browser that did not honour the App Link itself
 * (IP-2339: Opera keeps the redirect, Edge only after a prompt).
 *
 * The intent carries the same https App Link the app already claims, and the
 * explicit `package` means Android offers it to the iPractice app only, never to
 * another app that might claim a custom scheme. A browser without the app
 * installed is sent to the Play Store by Android.
 *
 * @param input - The portal host and the `code` and `state` B2C returned.
 * @returns The `intent://…#Intent;…;end` URL to navigate to.
 */
export const buildAndroidAppIntentUrl = ({ host, code, state }: AndroidAppIntentInput): string => {
  const query = new URLSearchParams({ code, state }).toString();
  return `intent://${host}${MOBILE_AUTH_PATH}?${query}#Intent;scheme=https;package=${ANDROID_APP_PACKAGE};end`;
};
